import {
  flushOfflineBootstrapPendingCounts,
  getSyncAttentionSnapshot,
  runManualOfflineSyncNow,
  type ManualOfflineSyncOutcome,
  type PendingActionsSnapshot,
} from 'services/offlineBootstrap'
import { syncAPI } from 'api/sync'
import { logPosAuditEvent } from 'services/posAuditLog'
import { publishPosDeviceSyncHeartbeat } from 'services/posDeviceSyncHeartbeat'
import {
  getQueue,
  dequeueMany,
  requeueFailedMutations,
  requeueConflictMutations,
} from 'store/offlineQueue'
import { replayQueue } from 'slices/offlineMiddleware'
import { flushTableOps } from 'services/TableSyncService'
import * as posStore from 'store/posStore'
import * as tableOps from 'store/tableOpsQueue'
import { checkConnectivity } from 'utils/connectivityCheck'
import { resolveOfflineScope } from 'store/posStore'

export type ShiftCloseSyncGate = {
  blocked: boolean
  snapshot: PendingActionsSnapshot
  message: string
  pendingCount: number
  failedCount: number
  conflictCount: number
}

export function buildShiftCloseSyncBlockerMessage(
  snap: PendingActionsSnapshot,
): string {
  const parts: string[] = []
  const pending =
    snap.pending_mutations + snap.pending_pos_orders + snap.pending_table_ops
  const failed = snap.failed_orders + snap.failed_table_ops + snap.failed_mutations
  const conflicts =
    snap.mutation_conflicts + snap.pos_order_conflicts + snap.table_op_conflicts

  if (pending > 0) {
    const detail: string[] = []
    if (snap.pending_pos_orders > 0) detail.push(`${snap.pending_pos_orders} طلب نقطة بيع`)
    if (snap.pending_table_ops > 0) detail.push(`${snap.pending_table_ops} عملية طاولة`)
    if (snap.pending_mutations > 0) detail.push(`${snap.pending_mutations} عملية أخرى`)
    parts.push(`قيد المزامنة: ${detail.join('، ')}`)
  }
  if (failed > 0) {
    const detail: string[] = []
    if (snap.failed_orders > 0) detail.push(`${snap.failed_orders} طلب فاشل`)
    if (snap.failed_table_ops > 0) detail.push(`${snap.failed_table_ops} عملية طاولة فاشلة`)
    if (snap.failed_mutations > 0) detail.push(`${snap.failed_mutations} عملية محفوظة فاشلة`)
    parts.push(detail.join('، '))
  }
  if (conflicts > 0) {
    parts.push(`تعارضات تحتاج مراجعة: ${conflicts}`)
  }

  if (parts.length === 0) {
    return ''
  }
  return `لا يمكن إغلاق الوردية — ${parts.join(' · ')}. أعد المزامنة، أو احذف الفاشل/تجاهل التعارض بعد التأكد من الخادم.`
}

function dispatchSyncAttentionEvents(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event('pos:failed-orders-updated'))
  window.dispatchEvent(new Event('pos:table-ops-updated'))
}

export async function evaluateShiftCloseSyncGate(): Promise<ShiftCloseSyncGate> {
  const snapshot = await getSyncAttentionSnapshot()
  const pendingCount =
    snapshot.pending_mutations +
    snapshot.pending_pos_orders +
    snapshot.pending_table_ops
  const failedCount =
    snapshot.failed_orders + snapshot.failed_table_ops + snapshot.failed_mutations
  const conflictCount =
    snapshot.mutation_conflicts +
    snapshot.pos_order_conflicts +
    snapshot.table_op_conflicts
  const blocked = pendingCount + failedCount + conflictCount > 0

  return {
    blocked,
    snapshot,
    message: buildShiftCloseSyncBlockerMessage(snapshot),
    pendingCount,
    failedCount,
    conflictCount,
  }
}

/** Re-queue failed items then run full offline sync (mutations, table ops, POS push). */
export async function retryAllSyncBeforeShiftClose(): Promise<ManualOfflineSyncOutcome> {
  const online = await checkConnectivity()
  if (!online) {
    throw new Error('لا يوجد اتصال بالإنترنت حاليًا')
  }

  await posStore.moveAllFailedOrdersToPending()

  const failedTableOps = await tableOps.getFailedTableOps()
  for (const entry of failedTableOps) {
    await tableOps.moveFailedTableOpToPending(entry.op.id)
  }

  await requeueFailedMutations()
  await requeueConflictSyncItems()

  return runManualOfflineSyncNow()
}

/** Move conflict rows back to pending for another sync attempt. */
export async function requeueConflictSyncItems(): Promise<void> {
  await requeueConflictMutations()

  await posStore.requeueConflictedPendingOrders()

  const tableQueue = await tableOps.getTableOpsQueue()
  for (const op of tableQueue) {
    if (op.status === 'conflict') {
      await tableOps.updateTableOpInQueue(op.id, { status: 'pending', last_error: null })
    }
  }

  const failedTableOps = await tableOps.getFailedTableOps()
  for (const entry of failedTableOps) {
    if (entry.op.status === 'conflict') {
      await tableOps.moveFailedTableOpToPending(entry.op.id)
    }
  }
}

export type ClearConflictSyncResult = {
  removedMutations: number
  removedPosOrders: number
  removedTableOps: number
  removedFailedTableOps: number
}

/**
 * Drop local conflict markers after manual resolution on the server.
 * Does not remove items still pending/syncing.
 */
export async function clearAllConflictSyncItems(): Promise<ClearConflictSyncResult> {
  const queue = await getQueue()
  const conflictMutationIds = new Set(
    queue.filter((m) => m.status === 'conflict').map((m) => m.id),
  )
  if (conflictMutationIds.size > 0) {
    await dequeueMany(conflictMutationIds)
  }

  const pendingOrders = await posStore.getPendingOrders()
  const conflictOrderUuids = pendingOrders
    .filter((o) => o.status === 'conflict')
    .map((o) => o.client_uuid)
  if (conflictOrderUuids.length > 0) {
    await posStore.removePendingByClientUuids(new Set(conflictOrderUuids))
  }

  const tableQueue = await tableOps.getTableOpsQueue()
  let removedTableOps = 0
  for (const op of tableQueue) {
    if (op.status === 'conflict') {
      await tableOps.removeTableOpById(op.id)
      removedTableOps += 1
    }
  }

  const failedTableOps = await tableOps.getFailedTableOps()
  let removedFailedTableOps = 0
  for (const entry of failedTableOps) {
    if (entry.op.status === 'conflict') {
      await tableOps.removeFailedTableOpById(entry.op.id)
      removedFailedTableOps += 1
    }
  }

  await flushOfflineBootstrapPendingCounts()
  dispatchSyncAttentionEvents()

  return {
    removedMutations: conflictMutationIds.size,
    removedPosOrders: conflictOrderUuids.length,
    removedTableOps,
    removedFailedTableOps,
  }
}

export type ClearFailedSyncResult = {
  removedOrders: number
  removedTableOps: number
  removedMutations: number
}

export type ReconcileFailedSyncResult = ClearFailedSyncResult & {
  unreconciledOrders: string[]
  unreconciledTableOps: string[]
  unreconciledMutations: string[]
}

export async function reconcileFailedPosOrder(
  clientUuid: string,
  order: posStore.PendingOfflineOrder,
): Promise<boolean> {
  const fixed = posStore.coercePendingOrderForSync(order)
  if (!fixed) return false

  // Never attach the current Shift while reconciling a historical local row.
  const toSend = fixed

  await logPosAuditEvent(clientUuid, 'reconcile_attempt', { branch_id: order.branch_id })

  try {
    const pushRes = await syncAPI.pushOfflineOrders([toSend], order.branch_id)
    const row = pushRes.data?.find((r) => r.client_uuid === clientUuid)
    if (row?.status === 'created' || row?.status === 'duplicate') {
      await logPosAuditEvent(clientUuid, 'push_confirmed', {
        branch_id: order.branch_id,
        via: 'reconcile_clear',
        status: row.status,
      })
      return true
    }
  } catch {
    /* fall through to retry */
  }

  try {
    const retryRes = await syncAPI.retryOfflineOrder(clientUuid, order.branch_id)
    if (retryRes.status === 'success') {
      await logPosAuditEvent(clientUuid, 'push_confirmed', {
        branch_id: order.branch_id,
        via: 'retry_offline_order',
      })
      return true
    }
  } catch {
    /* not on server or still failing */
  }

  await logPosAuditEvent(clientUuid, 'push_failed', {
    branch_id: order.branch_id,
    via: 'reconcile_clear',
  })
  return false
}

/**
 * Reconcile one failed POS order with the server and remove locally only on success.
 */
export async function reconcileAndClearSingleFailedPosOrder(
  entry: posStore.FailedOfflineOrder,
  options: { hasManageShifts: boolean },
): Promise<{ cleared: boolean; error?: 'permission' | 'offline' | 'unreconciled' | 'invalid' }> {
  if (!options.hasManageShifts) {
    return { cleared: false, error: 'permission' }
  }
  if (!(await checkConnectivity())) {
    return { cleared: false, error: 'offline' }
  }

  const uuid = entry.order.client_uuid
  const ok = await reconcileFailedPosOrder(uuid, entry.order)
  if (!ok) {
    return { cleared: false, error: 'unreconciled' }
  }

  await posStore.removeFailedByClientUuids(new Set([uuid]))
  await logPosAuditEvent(uuid, 'dismissed', {
    branch_id: entry.order.branch_id,
    reason: 'server_reconciled',
    via: 'failed_orders_dialog',
  })

  const scope = resolveOfflineScope()
  const branchId = scope.branchId !== 'unknown' ? scope.branchId : null
  if (branchId) {
    try {
      await syncAPI.logDismissedFailedSync(
        {
          client_uuids: [uuid],
          action: 'reconciled_and_cleared',
          reason: 'failed_orders_dialog',
        },
        branchId,
      )
    } catch {
      /* audit is best-effort */
    }
  }

  await publishPosDeviceSyncHeartbeat()
  return { cleared: true }
}

/**
 * Reconcile failed sync items with the server before clearing local dead-letter rows.
 * POS orders are removed only after server returns created/duplicate (or retry success).
 * Requires manage_shifts permission and an active connection.
 */
export async function reconcileFailedSyncItemsBeforeClear(options: {
  hasManageShifts: boolean
}): Promise<ReconcileFailedSyncResult> {
  if (!options.hasManageShifts) {
    throw new Error('MISSING_PERMISSION')
  }
  if (!(await checkConnectivity())) {
    throw new Error('OFFLINE')
  }

  const scope = resolveOfflineScope()
  const branchId = scope.branchId !== 'unknown' ? scope.branchId : null

  const reconciledOrderUuids: string[] = []
  const unreconciledOrders: string[] = []

  const failedOrders = await posStore.getFailedOrders()
  for (const entry of failedOrders) {
    const uuid = entry.order.client_uuid
    const ok = await reconcileFailedPosOrder(uuid, entry.order)
    if (ok) {
      reconciledOrderUuids.push(uuid)
      await logPosAuditEvent(uuid, 'dismissed', {
        branch_id: entry.order.branch_id,
        reason: 'server_reconciled',
      })
    } else {
      unreconciledOrders.push(uuid)
    }
  }

  if (reconciledOrderUuids.length > 0) {
    await posStore.removeFailedByClientUuids(new Set(reconciledOrderUuids))
    if (branchId) {
      try {
        await syncAPI.logDismissedFailedSync(
          {
            client_uuids: reconciledOrderUuids,
            action: 'reconciled_and_cleared',
          },
          branchId,
        )
      } catch {
        /* audit is best-effort */
      }
    }
  }

  const failedTableOpsBefore = await tableOps.getFailedTableOps()
  for (const entry of failedTableOpsBefore) {
    await tableOps.moveFailedTableOpToPending(entry.op.id)
  }
  if (failedTableOpsBefore.length > 0) {
    await flushTableOps()
  }
  const failedTableOpsAfter = await tableOps.getFailedTableOps()
  const removedTableOps = failedTableOpsBefore.length - failedTableOpsAfter.length
  const unreconciledTableOps = failedTableOpsAfter.map((e) => e.op.id)

  const queueBefore = await getQueue()
  const failedMutationIdsBefore = new Set(
    queueBefore.filter((m) => m.status === 'failed').map((m) => m.id),
  )
  if (failedMutationIdsBefore.size > 0) {
    await requeueFailedMutations()
    await replayQueue()
  }
  const queueAfter = await getQueue()
  const failedMutationIdsAfter = new Set(
    queueAfter.filter((m) => m.status === 'failed').map((m) => m.id),
  )
  const removedMutations = Array.from(failedMutationIdsBefore).filter(
    (id) => !failedMutationIdsAfter.has(id) && !queueAfter.some((m) => m.id === id),
  ).length
  const unreconciledMutations = Array.from(failedMutationIdsAfter)

  await flushOfflineBootstrapPendingCounts()
  await publishPosDeviceSyncHeartbeat()
  dispatchSyncAttentionEvents()

  return {
    removedOrders: reconciledOrderUuids.length,
    removedTableOps,
    removedMutations,
    unreconciledOrders,
    unreconciledTableOps,
    unreconciledMutations,
  }
}

/** @deprecated Use reconcileFailedSyncItemsBeforeClear — blind delete is no longer allowed. */
export async function clearAllFailedSyncItems(): Promise<ClearFailedSyncResult> {
  const result = await reconcileFailedSyncItemsBeforeClear({ hasManageShifts: true })
  return {
    removedOrders: result.removedOrders,
    removedTableOps: result.removedTableOps,
    removedMutations: result.removedMutations,
  }
}
